// src/pages/teacher/exams/TeacherExamTemplate.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Card, Row, Col, Form, Button, Badge, Alert, Spinner, ProgressBar,
} from "react-bootstrap";
import { v4 as uuid } from "uuid";
import { collection, doc, setDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

/* -------------------- Helpers -------------------- */
const TEST_TYPE_OPTIONS = [
  { value: "daily_test",         label: "Daily Test" },
  { value: "weekly_test",        label: "Weekly Test" },
  { value: "monthly_test",       label: "Monthly Test" },
  { value: "unit_test",          label: "Unit Test" },
  { value: "sectional_test",     label: "Sectional Test" },
  { value: "part_test",          label: "Part Test" },
  { value: "full_syllabus_test", label: "Full Syllabus Test" },
  { value: "mock_test",          label: "Mock Test" },
  { value: "sample_paper",       label: "Sample Paper" },
  { value: "previous_year_paper",label: "Previous Year Paper" },
  { value: "other",              label: "Other… (custom)" },
];
const testTypeLabel = (value, custom) =>
  value === "other" && custom?.trim()
    ? custom.trim()
    : (TEST_TYPE_OPTIONS.find(o => o.value === value)?.label || "Daily Test");

const DEFAULT_SECTION = (key = "A") => ({
  id: uuid(),
  key,
  label: key,
  questionCount: 0,
   marksPerCorrect: 1,
 negativeMarks: 0,
});
const nextSectionKey = (sections) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const used = new Set((sections || []).map((s) => (s.key || "").toUpperCase()));
  for (let i = 0; i < letters.length; i++) if (!used.has(letters[i])) return letters[i];
  return String((sections?.length || 0) + 1);
};
const DEFAULT_SUBJECT_ROW = () => ({
  id: uuid(),
  subjectId: "",
  subjectName: "",
  sections: [DEFAULT_SECTION("A")],
});

const DEFAULT_FORM = {
  name: "",
  examType: "objective",     // 'objective' | 'descriptive'
  testType: "daily_test",
  testTypeCustom: "",
  totalQuestions: 0,
  subjectWise: [DEFAULT_SUBJECT_ROW()],
  status: "draft",           // 'draft' | 'ready'
  allowNegativeMarking: true, 
  // attempt behavior
  randomizeQuestions: true,
  allowBackNavigation: true,
  showMarksPerQuestion: true,
  allowedDevices: ["web"],
  instructions: "",
  // descriptive settings
  onlineAttemptEnabled: true,
  requiresPdfUpload: false,
  pdfMaxSizeMB: 5,
  pdfInstructions: "",
  // readonly context fields (visible only)
  classIds: [],
  sectionIds: [],
  branchIds: [],
};

/* -------------------- Normalize (load) -------------------- */
function normalizeDocToForm(docData) {
  if (!docData) return DEFAULT_FORM;

  const safeSections = (secs) =>
    (secs || []).map((sec) => ({
      id: sec.id || uuid(),
      key: (sec.key || "A").toUpperCase(),
      label: sec.label || (sec.key || "A"),
      questionCount: +sec.questionCount || 0,
       marksPerCorrect: Number.isFinite(+sec.marksPerCorrect) ? +sec.marksPerCorrect : 1,
    negativeMarks: Number.isFinite(+sec.negativeMarks) ? +sec.negativeMarks : 0,
    }));

  const safeSubjects = (arr) =>
    (arr || []).map((s) => ({
      id: uuid(), // UI row id
      subjectId: String(s.subjectId || "").trim(),
      subjectName: String(s.subjectName || "").trim(),
      sections: safeSections(s.sections),
    }));

  const docTestType = docData.testType || docData.examMode || "daily_test";
  const docTestTypeCustom = docData.testTypeCustom || docData.examModeCustom || "";

  return {
    ...DEFAULT_FORM,
    name: docData.name || "",
    examType: docData.examType === "descriptive" ? "descriptive" : "objective",
    testType: docTestType,
    testTypeCustom: docTestTypeCustom,
    totalQuestions: Number.isFinite(+docData.totalQuestions) ? +docData.totalQuestions : 0,
    subjectWise: safeSubjects(docData.subjectWise),
    classIds: Array.isArray(docData.classIds) ? docData.classIds : [],
    sectionIds: Array.isArray(docData.sectionIds) ? docData.sectionIds : [],
    branchIds: Array.isArray(docData.branchIds) ? docData.branchIds : [],
    randomizeQuestions: !!docData.randomizeQuestions,
    allowBackNavigation: !!docData.allowBackNavigation,
    showMarksPerQuestion: !!docData.showMarksPerQuestion,
    allowedDevices: Array.isArray(docData.allowedDevices) ? docData.allowedDevices : ["web"],
    instructions: docData.instructions || "",
    status: docData.status || "draft",
    onlineAttemptEnabled: !!docData.onlineAttemptEnabled,
    requiresPdfUpload: !!docData.requiresPdfUpload,
    pdfMaxSizeMB: Number.isFinite(+docData.pdfMaxSizeMB) ? +docData.pdfMaxSizeMB : 5,
    pdfInstructions: docData.pdfInstructions || "",
    allowNegativeMarking: !!docData.allowNegativeMarking, // NEW
    _loadedTestTypeLabel: testTypeLabel(docTestType, docTestTypeCustom),
    _createdBy: docData.createdBy || docData.createdByUid || "",
  };
}

/* -------------------- Build payload (save) -------------------- */
function buildPayloadForSave(form, docId, userUid) {
  const normalizedSubjects = (form.subjectWise || []).map((s) => {
    const sections = (s.sections || []).map((sec) => ({
      id: sec.id,
      key: (sec.key || "").toUpperCase(),
      label: (sec.label || (sec.key || "")),
      questionCount: +sec.questionCount || 0,
      marksPerCorrect: Number.isFinite(+sec.marksPerCorrect) ? +sec.marksPerCorrect : 1,
     negativeMarks: Number.isFinite(+sec.negativeMarks) ? +sec.negativeMarks : 0,
    }));
    const perSubjectTotal = sections.reduce((a, sec) => a + (sec.questionCount || 0), 0);
    return {
      subjectId: String(s.subjectId || "").trim(),
      subjectName: String(s.subjectName || "").trim(),
      questionCount: perSubjectTotal,
      sections,
    };
  });

  const grandTotal = normalizedSubjects.reduce((a, s) => a + (s.questionCount || 0), 0);
  const isDescriptive = form.examType === "descriptive";
  const labelForType = testTypeLabel(form.testType, form.testTypeCustom);

  return {
    id: docId,
    name: form.name.trim(),
    examType: isDescriptive ? "descriptive" : "objective",

    // modern testType fields
    testType: form.testType,
    testTypeLabel: labelForType,
    testTypeCustom: form.testType === "other" ? (form.testTypeCustom || "") : "",

    totalQuestions: +form.totalQuestions,
    subjectWise: normalizedSubjects,
    totalSubjectQuestions: grandTotal,

    // keep existing target context (do NOT overwrite here)
    classIds: form.classIds || [],
    sectionIds: form.sectionIds || [],
    branchIds: form.branchIds || [],

    // attempt behavior
    onlineAttemptEnabled: !isDescriptive,
    requiresPdfUpload: isDescriptive,
    pdfMaxSizeMB: isDescriptive ? (+form.pdfMaxSizeMB || 5) : null,
    pdfInstructions: isDescriptive ? (form.pdfInstructions || "") : "",

    randomizeQuestions: !!form.randomizeQuestions,
    allowBackNavigation: !!form.allowBackNavigation,
    showMarksPerQuestion: !!form.showMarksPerQuestion,
    allowedDevices: form.allowedDevices || ["web"],
    instructions: form.instructions || "",
    status: form.status || "draft",
allowNegativeMarking: !!form.allowNegativeMarking, 
    updatedAt: serverTimestamp(),
    ...(form._createdBy ? {} : { createdBy: userUid || "unknown", createdAt: serverTimestamp() }),
  };
}

/* ======================= Component ======================= */
export default function TeacherExamTemplate() {
  const { id: templateId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const templateSeed = location.state?.templateSeed || null;

  const { schoolId, school, isActive: isActiveSchool, loading: schoolLoading } = useSchool();
  const { user, profile } = useAuth();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(Boolean(templateId && templateId !== "new" && !templateSeed));
  const [denied, setDenied] = useState(false);

  const isEdit = Boolean(templateId && templateId !== "new");

  /* Derived totals */
  const subjectSum = useMemo(() => {
    return (form.subjectWise || []).reduce((acc, subj) => {
      const secSum = (subj.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
      return acc + secSum;
    }, 0);
  }, [form.subjectWise]);

  const ratio =
    +form.totalQuestions > 0
      ? Math.min(100, Math.round((subjectSum / +form.totalQuestions) * 100))
      : 0;

  const canSave = useMemo(() => {
    if (!isActiveSchool) return false;
    if (!form.name?.trim()) return false;
    if (!(Number.isFinite(+form.totalQuestions) && +form.totalQuestions > 0)) return false;
    if (subjectSum !== +form.totalQuestions) return false;
    return true;
  }, [form, subjectSum, isActiveSchool]);

  /* helpers to update state */
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addSubjectRow = () => setForm((f) => ({ ...f, subjectWise: [...(f.subjectWise || []), DEFAULT_SUBJECT_ROW()] }));
  const removeSubjectRow = (id) =>
    setForm((f) => ({ ...f, subjectWise: (f.subjectWise || []).filter((r) => r.id !== id) }));
  const updateSubjectRow = (id, patch) =>
    setForm((f) => ({ ...f, subjectWise: (f.subjectWise || []).map((r) => (r.id === id ? { ...r, ...patch } : r)) }));

  const addSection = (subjectId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const key = nextSectionKey(s.sections || []);
        return { ...s, sections: [...(s.sections || []), DEFAULT_SECTION(key)] };
      });
      return { ...f, subjectWise };
    });
  };
  const removeSection = (subjectId, sectionId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const next = (s.sections || []).filter((sec) => sec.id !== sectionId);
        return { ...s, sections: next.length ? next : [DEFAULT_SECTION("A")] };
      });
      return { ...f, subjectWise };
    });
  };
  const updateSection = (subjectId, sectionId, patch) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const nextSecs = (s.sections || []).map((sec) =>
          sec.id === sectionId ? { ...sec, ...patch } : sec
        );
        return { ...s, sections: nextSecs };
      });
      return { ...f, subjectWise };
    });
  };

  /* Seed (fast paint) */
  useEffect(() => {
    if (templateSeed && isEdit) {
      setForm(normalizeDocToForm(templateSeed));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSeed, isEdit]);

  /* Live load + permissions */
  useEffect(() => {
    if (!schoolId || !isEdit) return;

    setLoadingDoc(!templateSeed);
    const ref = doc(db, "schools", schoolId, "examTemplates", templateId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          // ✅ Allow: admin-ish OR teacher OR creator
          const role = String(profile?.role || profile?.userRole || "").toLowerCase();
          const isAdminish = ["admin","schooladmin","owner","superadmin","school_admin"].includes(role);
          const isTeacher  = ["teacher","faculty"].includes(role);
          const isCreator  = (data.createdBy === user?.uid) || (data.createdByUid === user?.uid);
          setDenied(!(isAdminish || isTeacher || isCreator));
          setForm(normalizeDocToForm(data));
        }
        setLoadingDoc(false);
      },
      () => setLoadingDoc(false)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, templateId, isEdit, profile?.role, user?.uid]);

  /* Save */
  const doSave = async (goToQuestionsAfter = false) => {
    if (!schoolId) { alert("School not selected."); return; }
    if (!canSave) { alert("Complete required fields and fix validation errors."); return; }
    try {
      setSaving(true);
      const docRef = isEdit
        ? doc(db, "schools", schoolId, "examTemplates", templateId)
        : doc(collection(db, "schools", schoolId, "examTemplates"));
      const payload = buildPayloadForSave(form, docRef.id, user?.uid);
      await setDoc(docRef, payload, { merge: true });
      setSaving(false);
      if (goToQuestionsAfter) {
        nav(`/school/exams/templates/${docRef.id}/questions`);
        return;
      }
      alert(isEdit ? "Template updated." : "Template created.");
      if (!isEdit) setForm(DEFAULT_FORM);
    } catch (e) {
      setSaving(false);
      alert(e?.message || "Failed to save template.");
    }
  };

  /* UI */
  if (schoolLoading || loadingDoc) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }

  const disabled = saving || denied || !isActiveSchool;
  const isDescriptive = form.examType === "descriptive";

  return (
    <div className="container-fluid p-0">
      {/* Hero / Header (accented, dark/light aware) */}
      <div className="te-hero mb-3">
        <div className="te-hero-inner">
          <div>
            <div className="tdb-eyebrow">Teacher • Exams</div>
            <h5 className="m-0 fw-bold">{isEdit ? "Edit Exam Template" : "Create Exam Template"}</h5>
            <div className="text-muted small">Design your paper and control attempt behavior.</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="light" text="dark" className="border">School: {school?.name || "-"}</Badge>
            <Badge bg={isActiveSchool ? "success" : "secondary"}>
              {isActiveSchool ? "School Active" : "School Inactive"}
            </Badge>
            <Badge bg={String(form.status).toLowerCase() === "ready" ? "success" : "secondary"}>
              {String(form.status || "draft")}
            </Badge>
          </div>
        </div>
      </div>

      {(!isActiveSchool || denied) && (
        <Alert variant={denied ? "info" : "warning"} className="mb-3">
          {!isActiveSchool
            ? <>Your school is not <strong>active</strong>. Activate to create or edit templates.</>
            : <>View-only mode is disabled now—teachers can edit templates. If you still can’t, check Firestore rules.</>}
        </Alert>
      )}

      <Card className="border-0 shadow-sm te-card">
        <div className="card-particles" aria-hidden />
        <Card.Body>
          {/* Progress */}
          <Row className="g-3 mb-2">
            <Col md={8}>
              <div className="p-3 border rounded-3 bg-light-subtle">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="fw-semibold">Question Allocation</div>
                  <small className="text-muted">{subjectSum} of {+form.totalQuestions || 0}</small>
                </div>
                <ProgressBar now={ratio} label={`${ratio}%`} variant={ratio === 100 ? "success" : "info"} />
              </div>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="mark-ready"
                label="Mark template as Ready"
                disabled={disabled}
                checked={String(form.status).toLowerCase() === "ready"}
                onChange={(e) => update({ status: e.target.checked ? "ready" : "draft" })}
              />
            </Col>
          </Row>

          {/* Basics */}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Exam Name</Form.Label>
                <Form.Control
                  disabled={disabled}
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Mid-Term – Mathematics"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Exam Type</Form.Label>
                <Form.Select
                  disabled={disabled}
                  value={form.examType}
                  onChange={(e) => {
                    const nextType = e.target.value === "descriptive" ? "descriptive" : "objective";
                    update({
                      examType: nextType,
                      onlineAttemptEnabled: nextType !== "descriptive",
                      requiresPdfUpload: nextType === "descriptive",
                    });
                  }}
                >
                  <option value="objective">Objective (online attempt)</option>
                  <option value="descriptive">Descriptive (PDF answer upload)</option>
                </Form.Select>
                <div className="mt-2">
                  {isDescriptive ? (
                    <Badge bg="warning" text="dark">PDF upload required</Badge>
                  ) : (
                    <Badge bg="success">Online attempt</Badge>
                  )}
                </div>
              </Form.Group>
            </Col>
 <Col md={3} className="d-flex align-items-end">
   <Form.Group className="w-100">
     <Form.Check
       type="switch"
       id="neg-marking"
       label="Enable Negative Marking"
       disabled={disabled}
       checked={!!form.allowNegativeMarking}
       onChange={(e) => update({ allowNegativeMarking: e.target.checked })}
     />
   </Form.Group>
 </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Test Type</Form.Label>
                <Form.Select
                  disabled={disabled}
                  value={form.testType}
                  onChange={(e) => update({ testType: e.target.value })}
                >
                  {TEST_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
                {form.testType === "other" && (
                  <Form.Control
                    className="mt-2"
                    placeholder="Enter custom test type (e.g., 'Sunday Test')"
                    value={form.testTypeCustom}
                    onChange={(e) => update({ testTypeCustom: e.target.value })}
                    disabled={disabled}
                  />
                )}
              </Form.Group>
            </Col>

            {/* Totals */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>Total Questions</Form.Label>
                <Form.Control
                  disabled={disabled}
                  inputMode="numeric"
                  value={form.totalQuestions}
                  onChange={(e) => update({ totalQuestions: e.target.value.replace(/[^0-9]/g, "") })}
                />
                <Form.Text muted>
                  Sections sum: <strong>{subjectSum}</strong>{" "}
                  {subjectSum !== +form.totalQuestions && (
                    <Badge bg="warning" text="dark" className="ms-2">Mismatch</Badge>
                  )}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Descriptive-only */}
            {isDescriptive && (
              <>
                <Col xs={12}><hr className="my-2" /></Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Max PDF Size (MB)</Form.Label>
                    <Form.Control
                      disabled={disabled}
                      inputMode="numeric"
                      value={form.pdfMaxSizeMB}
                      onChange={(e) => update({ pdfMaxSizeMB: e.target.value.replace(/[^0-9]/g, "") })}
                      placeholder="5"
                    />
                  </Form.Group>
                </Col>
                <Col md={9}>
                  <Form.Group>
                    <Form.Label>PDF Upload Instructions</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      disabled={disabled}
                      value={form.pdfInstructions}
                      onChange={(e) => update({ pdfInstructions: e.target.value })}
                      placeholder="Scan pages clearly, merge into a single PDF, then upload."
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col xs={12}><hr /></Col>

            {/* Subject-wise Distribution */}
            <Col xs={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="m-0 fw-semibold">Subject-wise Distribution</Form.Label>
                <Button disabled={disabled} size="sm" variant="outline-primary" onClick={addSubjectRow}>
                  + Add Subject
                </Button>
              </div>

              {form.subjectWise.map((row) => {
                const perSubjectTotal = (row.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
                return (
                  <Card key={row.id} className="mb-3 shadow-sm te-subject-card">
                    <Card.Header className="d-flex align-items-center gap-2">
                      <div className="flex-grow-1">
                        <div className="small text-muted">Subject</div>
                        <div className="fw-semibold">{row.subjectName || "Untitled"}</div>
                      </div>
                      <Badge bg="light" text="dark">Subject Total: {perSubjectTotal}</Badge>
                      {form.subjectWise.length > 1 && (
                        <Button
                          disabled={disabled}
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeSubjectRow(row.id)}
                          title="Remove subject"
                        >
                          ✕
                        </Button>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-2 align-items-end">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Subject Name</Form.Label>
                            <Form.Control
                              disabled={disabled}
                              value={row.subjectName}
                              onChange={(e) => updateSubjectRow(row.id, { subjectName: e.target.value })}
                              placeholder="Physics"
                            />
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Subject ID (optional)</Form.Label>
                            <Form.Control
                              disabled={disabled}
                              value={row.subjectId}
                              onChange={(e) => updateSubjectRow(row.id, { subjectId: e.target.value })}
                              placeholder="physics-12"
                            />
                          </Form.Group>
                        </Col>

                        <Col md={2} className="d-flex align-items-end">
                          <Button
                            disabled={disabled}
                            size="sm"
                            variant="outline-success"
                            className="ms-auto"
                            onClick={() => addSection(row.id)}
                          >
                            + Add Section
                          </Button>
                        </Col>

                        <Col xs={12}><hr className="my-2" /></Col>

                        {(row.sections || []).map((sec) => (
                          <Row key={sec.id} className="g-2 align-items-end mb-2">
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Key</Form.Label>
                                <Form.Control
                                  disabled={disabled}
                                  value={sec.key}
                                  onChange={(e) => {
                                    const newKey = e.target.value.toUpperCase().slice(0, 3);
                                    const syncLabel = (!sec.label || sec.label.toUpperCase() === String(sec.key || "").toUpperCase());
                                    updateSection(row.id, sec.id, {
                                      key: newKey,
                                      ...(syncLabel ? { label: newKey } : {}),
                                    });
                                  }}
                                  placeholder="A"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Label (optional)</Form.Label>
                                <Form.Control
                                  disabled={disabled}
                                  value={sec.label}
                                  onChange={(e) => updateSection(row.id, sec.id, { label: e.target.value })}
                                  placeholder="A"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={3}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Questions</Form.Label>
                                <Form.Control
                                  disabled={disabled}
                                  inputMode="numeric"
                                  value={sec.questionCount}
                                  onChange={(e) =>
                                    updateSection(row.id, sec.id, {
                                      questionCount: e.target.value.replace(/[^0-9]/g, ""),
                                    })
                                  }
                                  placeholder="10"
                                />
                              </Form.Group>
                            </Col>
<Col md={2}>
   <Form.Group>
     <Form.Label className="small mb-1">Marks / Correct</Form.Label>
     <Form.Control
       disabled={disabled}
       inputMode="numeric"
       value={sec.marksPerCorrect ?? 1}
       onChange={(e) =>
         updateSection(row.id, sec.id, {
           marksPerCorrect: e.target.value.replace(/[^0-9.]/g, ""),
         })
       }
       placeholder="1"
     />
   </Form.Group>
 </Col>

 <Col md={2}>
   <Form.Group>
     <Form.Label className="small mb-1">Negative Marks</Form.Label>
     <Form.Control
       disabled={disabled}
       inputMode="numeric"
       value={sec.negativeMarks ?? 0}
       onChange={(e) =>
         updateSection(row.id, sec.id, {
           negativeMarks: e.target.value.replace(/[^-0-9.]/g, ""), // allow minus
         })
       }
       placeholder="0"
     />
     
   </Form.Group>
</Col>
                            <Col md={1} className="text-end">
                              {(row.sections || []).length > 1 && (
                                <Button
                                  disabled={disabled}
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeSection(row.id, sec.id)}
                                  title="Remove section"
                                >
                                  ✕
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </Col>

            <Col xs={12}><hr /></Col>

            {/* Behavior toggles */}
            <Col md={4}>
              <Form.Check
                type="switch"
                id="rand-qs"
                label="Randomize Questions"
                disabled={disabled || isDescriptive}
                checked={form.randomizeQuestions}
                onChange={(e) => update({ randomizeQuestions: e.target.checked })}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="switch"
                id="back-nav"
                label="Allow Back Navigation"
                disabled={disabled}
                checked={form.allowBackNavigation}
                onChange={(e) => update({ allowBackNavigation: e.target.checked })}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="switch"
                id="show-marks"
                label="Show Marks/Question (display only)"
                disabled={disabled}
                checked={form.showMarksPerQuestion}
                onChange={(e) => update({ showMarksPerQuestion: e.target.checked })}
              />
            </Col>

            <Col xs={12}>
              <Form.Group className="mt-2">
                <Form.Label>Instructions (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  disabled={disabled}
                  value={form.instructions}
                  onChange={(e) => update({ instructions: e.target.value })}
                  placeholder="Read all questions carefully..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>

        {/* Sticky footer actions */}
        <div className="position-sticky bottom-0 bg-body border-top p-3">
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Button className="btn-accent" disabled={!canSave || saving || denied} onClick={() => doSave(false)}>
              {saving ? "Saving..." : isEdit ? "Update Template" : "Save Template"}
            </Button>

            <Button
              variant="success"
              disabled={!canSave || saving || denied}
              onClick={() => doSave(true)}
              title={isEdit ? "Update and manage questions" : "Save and go add questions"}
            >
              {saving ? "Saving..." : isEdit ? "Update & Manage Questions" : "Save & Add Questions"}
            </Button>

            {!isEdit && (
              <Button variant="outline-secondary" disabled={saving || denied} onClick={() => setForm(DEFAULT_FORM)}>
                Reset
              </Button>
            )}

            <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
              <Badge bg={subjectSum === +form.totalQuestions && +form.totalQuestions > 0 ? "success" : "warning"}>
                Sections Sum: {subjectSum} / Total: {+form.totalQuestions || 0}
              </Badge>
              <Badge bg="info" title={form.testType === "other" ? form.testTypeCustom : testTypeLabel(form.testType)}>
                Test Type: {testTypeLabel(form.testType, form.testTypeCustom)}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Local styles (accented, motion-safe, dark/light) */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .te-card,
          .te-card:hover,
          .card-particles {
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }
        .tdb-eyebrow { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--bs-secondary-color); }

        /* ===== Hero with fallbacks ===== */
        .te-hero {
          border: 1px solid var(--border);
          border-radius: 16px;
          background:
            radial-gradient(60% 120% at 10% -20%, rgba(var(--brand-accent-rgb, 106,140,255), .16) 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, rgba(var(--brand-accent-rgb, 106,140,255), .10) 0%, transparent 60%),
            var(--surface, #fff);
        }
        @supports (background: color-mix(in oklab, white 50%, black)) {
          .te-hero {
            background:
              radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
              radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
              color-mix(in oklab, var(--surface) 96%, transparent);
          }
        }
        .te-hero-inner { display:flex; align-items:center; justify-content:space-between; padding: 14px 16px; border-radius: 16px; }

        .te-card {
          position: relative;
          border-radius: 16px;
        }
        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(color-mix(in oklab, var(--brand-primary) 18%, transparent) 1px, transparent 1px),
            radial-gradient(color-mix(in oklab, var(--brand-accent) 18%, transparent) 1px, transparent 1px);
          background-position: 0 0, 12px 12px; background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite; opacity: .18;
          border-radius: 16px;
        }
        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }

        .btn-accent {
          --accent: var(--brand-accent);
          background-color: var(--accent) !important;
          border-color: var(--accent) !important;
          color: #fff !important;
          box-shadow: 0 .25rem .6rem rgba(var(--brand-accent-rgb, 106,140,255), .35);
        }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: #fff !important; }
      `}</style>
    </div>
  );
}
